import {
  Component,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  MultimediaIcon,
  MultimediaList,
} from '../../../shared/interfaces/multimedia-list.interface';
import { IconComponent } from '../../atoms/icon/icon.component';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { TooltipComponent } from '../../molecules/tooltip/tooltip.component';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { ModalType } from '../../../shared/enums/modal-type.enums';

import { Tooltip } from '../../../shared/interfaces/tooltip.interface';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import {
  FaIconLibrary,
  IconName,
  IconPrefix,
} from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { CaasMultimediaListLink } from '../../../shared/interfaces/caas/caas.interface';
import { ButtonComponent } from '../../molecules/button/button.component';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { CaasRoutesService } from '../../../services/caas-routes.service';

@Component({
  selector: 'hdi-multimedia-list',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    BadgeComponent,
    TooltipComponent,
    RouterModule,
    CapitalizePipe,
    ButtonComponent,
  ],
  templateUrl: './multimedia-list.component.html',
  styleUrls: ['./multimedia-list.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class MultimediaListComponent implements OnDestroy, OnChanges {
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() multimediaList: MultimediaList;
  @Input() backgroundColor!: 'white' | 'grey';
  @Input() caasData?: CaasComponent;
  @Input() sectionBg?: 'white' | 'grey';
  private modalVideoSubscription$: Subscription = new Subscription();
  showModal: boolean = false;
  tooltip: Tooltip;
  clipboardTooltip?: Tooltip;

  Sizes = Sizes;
  Colors = Colors;
  BadgeType = BadgeType;
  ModalType = ModalType;
  ButtonType = ButtonType;

  constructor(
    private library: FaIconLibrary,
    private caasRoutesService: CaasRoutesService,
    private router: Router
  ) {
    this.multimediaList = {
      title: '',
      key: '',
      items: [],
    };
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData'] && this.caasData) {
      this.multimediaList = this.setCaasContent();
      this.backgroundColor = this.sectionBg === 'grey' ? 'grey' : 'white';
    }
  }

  private setCaasContent(): MultimediaList {
    const multimediaList: MultimediaList = {
      title: this.caasData?.formData?.st_header?.value ?? '',
      key: this.caasData?.identifier ?? '',
      items: [],
    };

    const links = this.caasData?.formData?.st_links?.value ?? [];
    multimediaList.items = links.map((link) => ({
      label: link?.formData?.lt_link_label?.value || '',
      link:
        link?.formData?.lt_link_external?.value ??
        link?.formData?.lt_link_internal?.value?.url ??
        '',
      title: link?.formData?.lt_link_title?.value || '',
      leftIcon: this.getMultimediaLeftIcon(
        link?.formData?.lt_icon?.value?.identifier || ''
      ),
      rightIcons: this.getMultimediaRightIcons(link),
    }));

    return multimediaList;
  }

  getMultimediaRightIcons(link: CaasMultimediaListLink) {
    if (link?.formData?.lt_link_type?.value?.identifier) {
      switch (link.formData.lt_link_type.value.identifier) {
        case 'external':
          return [
            {
              type: 'arrow-up-right-from-square',
              href: link?.formData?.lt_link_external?.value ?? '',
              target: '_blank',
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>Öffnen</span>',
              },
              title: 'Öffnen',
            },
            {
              type: 'link',
              href: link?.formData?.lt_link_external?.value ?? '',
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>Link kopieren</span>',
              },
              title: 'Link kopieren',
            },
          ];
        case 'internal':
          return [
            {
              type: 'right',
              href:
                this.caasRoutesService.getSeoRouteByIdentifier(
                  link?.formData?.lt_link_internal?.value?.identifier ?? '',
                  this.router.config
                )?.path ?? '/404',
              target: '_self',
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>Öffnen</span>',
              },
              title: 'Öffnen',
            },
            {
              type: 'link',
              href:
                window.location.origin +
                '/' +
                this.caasRoutesService.getSeoRouteByIdentifier(
                  link?.formData?.lt_link_internal?.value?.identifier ?? '',
                  this.router.config
                )?.path,
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>Link kopieren</span>',
              },
              title: 'Link kopieren',
            },
          ];
        case 'contact': {
          const rightIcons: MultimediaIcon[] = [];
          if (link?.formData?.lt_email_address?.value) {
            rightIcons.push({
              type: 'envelope',
              href: 'mailto:' + link?.formData?.lt_email_address?.value,
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>E-mail senden</span>',
              },
              title: 'E-mail senden',
            });
          }
          if (link?.formData?.lt_telephone?.value) {
            rightIcons.push({
              type: 'phone',
              href: 'tel:' + link?.formData?.lt_telephone?.value,
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>Anrufen</span>',
              },
              title: 'Anrufen',
            });
          }
          return rightIcons;
        }
        case 'download':
          if (link?.formData?.lt_link_external?.value) {
            if (
              link?.formData?.lt_link_external?.value.includes(
                'https://www.hdi-brandportal.com'
              )
            ) {
              return [
                {
                  type: 'eye',
                  href: link?.formData?.lt_link_external?.value ?? '',
                  tooltip: {
                    id: crypto.randomUUID().toString(),
                    description: '<span>Ansicht</span>',
                  },
                  title: 'Ansicht',
                },
                {
                  type: 'link',
                  href: link?.formData?.lt_link_external?.value ?? '',
                  tooltip: {
                    id: crypto.randomUUID().toString(),
                    description: '<span>Link kopieren</span>',
                  },
                  title: 'Link kopieren',
                },
              ];
            } else {
              return [
                {
                  type: 'eye',
                  href: link?.formData?.lt_link_external?.value ?? '',
                  tooltip: {
                    id: crypto.randomUUID().toString(),
                    description: '<span>Ansicht</span>',
                  },
                  title: 'Ansicht',
                },
                {
                  type: 'down-to-line',
                  href: link?.formData?.lt_link_external?.value ?? '',
                  tooltip: {
                    id: crypto.randomUUID().toString(),
                    description: '<span>Download</span>',
                  },
                  title: 'Download',
                },
                {
                  type: 'link',
                  href: link?.formData?.lt_link_external?.value ?? '',
                  tooltip: {
                    id: crypto.randomUUID().toString(),
                    description: '<span>Link kopieren</span>',
                  },
                  title: 'Link kopieren',
                },
              ];
            }
          }
      }
    }
    return [];
  }

  getMultimediaLeftIcon(leftIcon: string) {
    if (leftIcon.length) {
      switch (leftIcon) {
        case 'file-pdf':
          return 'file-pdf';
        default:
          return leftIcon;
      }
    } else {
      return '';
    }
  }
  openModal(
    event: Event,
    icon: {
      type: string;
      href: string;
      target?: string;
      videoSrc?: string;
      thumbnailSrc?: string;
      videoId?: string;
    }
  ) {
    if (icon.videoSrc) {
      event.preventDefault();
      event.stopPropagation();
      // this.showModal = true;
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('openVideoModal', {
          detail: {
            videoSrc: icon.videoSrc,
            videoId: icon.videoId,
            thumbnailSrc: icon.thumbnailSrc,
          },
        });
        window.dispatchEvent(event);
      }
    }
  }

  copyToClipboard(icon: MultimediaIcon, event: TouchEvent | MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(icon.href);
    this.clipboardTooltip = {
      id: icon?.tooltip?.id ?? '',
      description: '<span>URL kopiert</span>',
    };
    setTimeout(() => {
      this.clipboardTooltip = undefined;
    }, 500);
  }

  openTooltip(tooltip: Tooltip) {
    this.tooltip = tooltip;
  }

  closeTooltip() {
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  getIconName(name: string): string {
    if (
      this.library.getIconDefinition('far' as IconPrefix, name as IconName) !==
      null
    ) {
      return name;
    } else {
      return 'user';
    }
  }

  ngOnDestroy() {
    this.modalVideoSubscription$.unsubscribe();
  }
}
